import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, errDetail, fmtISTTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CalendarClock, CheckCircle2, AlertCircle, RefreshCcw, X } from "lucide-react";
import { useSlotPicker } from "@/hooks/useSlotPicker";

/*
 * PUBLIC customer-facing booking page — mobile-first, tokenized, no login.
 * No internal IDs or staff data are exposed. Repeated taps are idempotent.
 */
export default function PublicBookingPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [day, setDay] = useState(null);
  const [busy, setBusy] = useState(false);
  const submitGuard = useRef(false);        // R15 U1: a double tap while the booking is in flight must not post it twice (the API is idempotent, this also stops the second round trip)
  const [confirmation, setConfirmation] = useState(null);
  const [mode, setMode] = useState("book"); // book | manage | reschedule
  const [cancelConfirming, setCancelConfirming] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const load = useCallback(() => {
    api.get(`/public/booking/${token}`)
      .then((r) => {
        setInfo(r.data);
        setMode(r.data.active_appointment ? "manage" : "book");
      })
      .catch((e) => setError(errDetail(e, "This booking link is not valid.")));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // R15 U1: the chosen time belongs to the exact (day, mode) it was fetched for - a different day invalidates it immediately, a late response is ignored, and "could not load" is not
  // shown as "no times left".
  const picker = useSlotPicker({
    key: day ? `${token}|${day}|${mode}` : null, date: day,
    load: async () => (await api.get(`/public/booking/${token}/slots?date=${day}`)).data,
  });
  const slot = picker.slot;

  const submit = async () => {
    if (!slot || submitGuard.current) return;
    submitGuard.current = true;
    setBusy(true);
    try {
      const endpoint = mode === "reschedule" ? "reschedule" : "book";
      const { data } = await api.post(`/public/booking/${token}/${endpoint}`, { start: slot });
      setConfirmation(data);
    } catch (e) {
      setError(errDetail(e));
      setTimeout(() => { setError(null); load(); setDay(null); }, 4000);
    } finally { setBusy(false); submitGuard.current = false; }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/public/booking/${token}/cancel`, { reason: cancelReason.trim() || null });
      setConfirmation({ status: "cancelled" });
    } catch (e) {
      setError(errDetail(e));
      setTimeout(() => setError(null), 4000);
    } finally { setBusy(false); }
  };

  const weekdayLabel = (iso) => {
    const d = new Date(iso + "T12:00:00");
    return { day: d.toLocaleDateString("en-IN", { weekday: "short" }), date: d.getDate(), month: d.toLocaleDateString("en-IN", { month: "short" }) };
  };

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--muted))]/40 px-4">
        <Card className="w-full max-w-md"><CardContent className="pt-8 pb-8 text-center">
          <AlertCircle className="h-8 w-8 text-[hsl(var(--danger))] mx-auto mb-3" />
          <p data-testid="public-booking-error" className="text-sm">{error}</p>
          <p className="text-xs text-muted-foreground mt-2">Please contact Aussie Pathways for a new booking link.</p>
        </CardContent></Card>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-3"><Skeleton className="h-24" /><Skeleton className="h-40" /></div>
      </div>
    );
  }

  if (confirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--muted))]/40 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center" data-testid="public-booking-confirmation">
            <CheckCircle2 className="h-10 w-10 text-[hsl(var(--success))] mx-auto mb-3" />
            {confirmation.status === "cancelled" ? (
              <>
                <h2 className="font-heading text-lg font-semibold">Consultation cancelled</h2>
                <p className="text-sm text-muted-foreground mt-2">You can use this same link to book a new time whenever you're ready.</p>
              </>
            ) : (
              <>
                <h2 className="font-heading text-lg font-semibold">{confirmation.status === "rescheduled" ? "Consultation rescheduled" : "Consultation confirmed"}</h2>
                <p data-testid="confirmation-time" className="text-sm mt-2 font-medium">{confirmation.label}</p>
                <p className="text-xs text-muted-foreground mt-3">We'll remind you before your consultation. Need to change it? Just open this link again.</p>
              </>
            )}
            <Button data-testid="confirmation-done-button" variant="outline" className="mt-5" onClick={() => { setConfirmation(null); setDay(null); load(); }}>Done</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--muted))]/40 px-4 py-8 flex justify-center">
      <div className="w-full max-w-md space-y-4" data-testid="public-booking-page">
        <div className="text-center">
          <h1 className="font-heading text-xl font-semibold">Aussie Pathways</h1>
          <p className="text-sm text-muted-foreground mt-1">Hi {info.first_name}, {mode === "manage" ? "manage your consultation below." : `book your ${info.appointment_type.toLowerCase()}.`}</p>
        </div>

        {error && (
          <div data-testid="public-booking-inline-error" className="text-sm bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] rounded-md px-3 py-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        {mode === "manage" && info.active_appointment ? (
          <Card>
            <CardContent className="pt-6 pb-6 text-center space-y-4">
              <CalendarClock className="h-8 w-8 mx-auto text-[hsl(var(--info))]" />
              <div>
                <p className="text-sm text-muted-foreground">Your consultation is booked for</p>
                <p data-testid="manage-current-time" className="font-medium mt-1">
                  {new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", hour12: true }).format(new Date(info.active_appointment.start))} IST
                </p>
              </div>
              <p className="text-xs text-muted-foreground">{info.policy}</p>
              {!cancelConfirming ? (
                <div className="flex gap-2 justify-center">
                  <Button data-testid="manage-reschedule-button" variant="outline" size="sm" onClick={() => setMode("reschedule")}>
                    <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Reschedule
                  </Button>
                  {info.cancel_enabled && (
                    <Button data-testid="manage-cancel-button" variant="ghost" size="sm" onClick={() => setCancelConfirming(true)}>
                      <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2 text-left" data-testid="manage-cancel-confirm">
                  <p className="text-xs text-center">Are you sure you want to cancel this consultation?</p>
                  <Input data-testid="manage-cancel-reason-input" placeholder="Reason (optional)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                  <div className="flex gap-2 justify-center">
                    <Button data-testid="manage-cancel-back-button" variant="ghost" size="sm" onClick={() => { setCancelConfirming(false); setCancelReason(""); }}>Never mind</Button>
                    <Button data-testid="manage-cancel-confirm-button" variant="destructive" size="sm" disabled={busy} onClick={cancel}>
                      Confirm cancellation
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">1. Pick a day <span className="normal-case font-normal">(times in IST)</span></p>
                <div className="flex gap-1.5 overflow-x-auto thin-scroll pb-1">
                  {info.days.map((d) => {
                    const w = weekdayLabel(d.date);
                    return (
                      <button
                        key={d.date}
                        data-testid={`day-chip-${d.date}`}
                        disabled={!d.has_slots}
                        onClick={() => setDay(d.date)}
                        className={`shrink-0 w-14 rounded-lg border px-1 py-2 text-center transition-colors ${
                          day === d.date ? "bg-primary text-primary-foreground border-primary"
                            : d.has_slots ? "bg-card hover:bg-muted" : "bg-muted/60 text-muted-foreground/40 cursor-not-allowed"
                        }`}
                      >
                        <div className="text-[10px] uppercase">{w.day}</div>
                        <div className="text-base font-semibold">{w.date}</div>
                        <div className="text-[10px]">{w.month}</div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {day && (
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">2. Pick a time · {info.duration_min} minutes</p>
                  {picker.status === "loading" ? (
                    <div className="grid grid-cols-3 gap-2">{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-10" />)}</div>
                  ) : picker.status === "empty" ? (
                    <p data-testid="public-no-slots" className="text-sm text-muted-foreground text-center py-4">No times left on this day — please pick another.</p>
                  ) : picker.status === "forbidden" || picker.status === "error" ? (
                    <div data-testid="public-slots-error" role="alert" className="text-sm text-center py-4 space-y-2">
                      <p>We could not load the available times just now. This does not mean the day is full.</p>
                      <Button size="sm" variant="outline" data-testid="public-slots-retry" onClick={picker.reload}>Try again</Button>
                    </div>
                  ) : picker.status === "ready" ? (
                    <div className="grid grid-cols-3 gap-2">
                      {picker.slots.map((s) => (
                        <button
                          key={s.start}
                          data-testid={`slot-button-${s.label.replace(/[: ]/g, "")}`}
                          onClick={() => picker.select(s.start)}
                          className={`h-10 rounded-md border text-sm font-medium transition-colors ${slot === s.start ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {slot && (
              <Button data-testid="public-confirm-button" className="w-full h-11" disabled={busy} onClick={submit}>
                {busy ? "Confirming…" : mode === "reschedule" ? "Confirm new time" : "Confirm consultation"}
              </Button>
            )}
            {mode === "reschedule" && (
              <Button variant="ghost" className="w-full" size="sm" onClick={() => { setMode("manage"); setDay(null); }}>Back</Button>
            )}
          </>
        )}
        <p className="text-[11px] text-center text-muted-foreground">Aussie Pathways · Consultations run in Indian Standard Time</p>
      </div>
    </div>
  );
}
